<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Evidence and Traceability for GMP AI Workflows — QikSolve',
  description:
    'How GMP teams can build practical AI governance evidence and traceability so AI use, output, review, and resulting decisions remain understandable.',
});

const page = {
  hero: {
    eyebrow: 'Evidence and traceability',
    title: 'Make AI-assisted quality work understandable',
    summary:
      'Trust grows when a team can reconstruct what AI contributed, what was reviewed, who decided, and what evidence supports the result under an AI governance model.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'Traceability supports both control and confidence',
    content:
      'Evidence is not only something to show during an inspection. It helps people review work consistently, understand uncertainty, investigate exceptions, and improve the operating model over time.',
    items: [
      { title: 'Context', description: 'Keep the intended use, input context, source references, and output class visible.' },
      { title: 'Review', description: 'Record who reviewed the output, what they decided, and why.' },
      { title: 'Change', description: 'Retain relevant model, prompt, rules, retrieval, and workflow version context.' },
    ],
  },
  sections: [
    {
      title: 'What a useful evidence chain can show',
      items: [
        { title: 'What happened', description: 'Identify the workflow, input, AI contribution, output, and downstream action.' },
        { title: 'Who was accountable', description: 'Make initiation, review, acceptance, rejection, escalation, and approval attributable.' },
        { title: 'What was known', description: 'Preserve source evidence, uncertainty, missing information, and relevant version context.' },
        { title: 'What changed', description: 'Connect material changes to review, evaluation, rollback, and follow-up decisions.' },
      ],
    },
    {
      title: 'Proportionate evidence by output impact',
      content:
        'Informational support does not require the same evidence chain as output incorporated into a controlled record. The planning model should help the team collect the right evidence for the process impact without treating every output identically.',
      items: [
        { title: 'Understandable', description: 'A reviewer can follow the path from input to output to decision.' },
        { title: 'Retrievable', description: 'The evidence can be found and reviewed when the quality team needs it.' },
      ],
    },
  ],
  callout: {
    title: 'Evidence should help the team work better',
    content: 'QikSolve helps teams design evidence and review practices that fit their quality process. QxAIOS provides deeper platform and product detail where required.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Human oversight patterns', description: 'Design the review workflow that produces meaningful evidence.', to: '/product/human-oversight-for-gmp-ai-workflows' },
      { label: 'Regulatory compliance', description: 'Explore the wider validation and evidence context.', to: '/product/regulatory-compliance' },
      { label: 'Explore QxAIOS', description: 'Visit the QxAIOS solution for platform and product depth.', to: 'https://qx.qiksolve.com' },
    ],
  },
  cta: { title: 'Need to strengthen AI evidence?', content: 'Bring the workflow, output, reviewer, and retrieval questions your team is working through.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
